import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera,
  BrainCircuit,
  ChevronRight,
  Wifi,
  Clock,
  Sparkles,
  Radio,
  Play,
  LucideIcon,
} from 'lucide-react';
import cameraImg from '../../assets/images/camera.png';

// =========================================
// 1. CONFIGURATION & DATA TYPES
// =========================================

export type ProductId = 'camera' | 'ia';

export interface FeatureMetric {
  label: string;
  value: number;
  icon: LucideIcon;
}

export interface ProductData {
  id: ProductId;
  label: string;
  title: string;
  description: string;
  image: string;
  colors: {
    gradient: string;
    glow: string;
    ring: string;
  };
  stats: {
    connectionStatus: string;
  };
  features: FeatureMetric[];
}

const PRODUCT_DATA: Record<ProductId, ProductData> = {
  camera: {
    id: 'camera',
    label: 'Câmera',
    title: 'Câmera Apertai',
    description:
      'Gravação inteligente e contínua em Full HD. A câmera captura automaticamente os últimos 30 segundos de cada jogada marcante. Resistente à água e ao sol, funciona 24 horas por dia sem intervenção — basta instalar e deixar a tecnologia trabalhar.',
    image: cameraImg,
    colors: {
      gradient: 'from-orange-500 to-amber-700',
      glow: 'bg-orange-500',
      ring: 'border-l-orange-500/50',
    },
    stats: { connectionStatus: 'Gravando' },
    features: [
      { label: 'Qualidade', value: 95, icon: Play },
      { label: 'Cobertura', value: 100, icon: Wifi },
    ],
  },
  ia: {
    id: 'ia',
    label: 'Inteligência',
    title: 'Inteligência Apertai',
    description:
      'Usamos inteligência artificial para melhorar cada aspecto do produto. Lives automáticas com enquadramento inteligente, processamento de vídeo otimizado por IA, e inovações constantes — estamos sempre criando novas formas de elevar a experiência na arena.',
    image: '',
    colors: {
      gradient: 'from-violet-500 to-purple-800',
      glow: 'bg-violet-500',
      ring: 'border-r-violet-500/50',
    },
    stats: { connectionStatus: 'Aprendendo' },
    features: [
      { label: 'Automação', value: 96, icon: Sparkles },
      { label: 'Lives IA', value: 90, icon: Radio },
    ],
  },
};

// =========================================
// 2. ANIMATION VARIANTS
// =========================================

const ANIMATIONS: any = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 20, filter: 'blur(10px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { type: 'spring' as const, stiffness: 100, damping: 20 },
    },
    exit: { opacity: 0, y: -10, filter: 'blur(5px)' },
  },
  image: (isLeft: boolean) => ({
    initial: {
      opacity: 0,
      scale: 1.5,
      filter: 'blur(15px)',
      rotate: isLeft ? -30 : 30,
      x: isLeft ? -80 : 80,
    },
    animate: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      rotate: 0,
      x: 0,
      transition: { type: 'spring', stiffness: 260, damping: 20 },
    },
    exit: {
      opacity: 0,
      scale: 0.6,
      filter: 'blur(20px)',
      transition: { duration: 0.25 },
    },
  }),
};

// =========================================
// 3. SUB-COMPONENTS
// =========================================

const BackgroundGradient = ({ isLeft }: { isLeft: boolean }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <motion.div
      animate={{
        background: isLeft
          ? 'radial-gradient(circle at 0% 50%, rgba(245, 138, 37, 0.15), transparent 50%)'
          : 'radial-gradient(circle at 100% 50%, rgba(139, 92, 246, 0.15), transparent 50%)',
      }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0"
    />
  </div>
);

const CameraVisual = () => (
  <div className="relative h-60 w-60 md:h-80 md:w-80 rounded-full flex items-center justify-center overflow-hidden">
    <motion.div
      animate={{
        background: [
          'linear-gradient(135deg, #78350f 0%, #1c1917 50%, #c2410c 100%)',
          'linear-gradient(135deg, #c2410c 0%, #78350f 50%, #1c1917 100%)',
          'linear-gradient(135deg, #1c1917 0%, #c2410c 50%, #78350f 100%)',
        ],
      }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
      className="absolute inset-0 rounded-full"
    />
    <div className="absolute inset-0 border border-white/10 rounded-full" />

    <motion.div
      animate={{ y: [-8, 8, -8] }}
      transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
      className="relative z-10 flex flex-col items-center gap-4"
    >
      <div className="relative">
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -inset-4 bg-orange-500/30 rounded-full blur-2xl"
        />
        <Camera className="w-20 h-20 md:w-28 md:h-28 text-orange-300 relative z-10" />
      </div>
      <div className="flex items-center gap-2">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="h-2 w-2 rounded-full bg-red-500"
        />
        <span className="text-xs text-orange-300 font-medium tracking-wider uppercase">
          Gravando
        </span>
      </div>
    </motion.div>
  </div>
);

const IAVisual = () => (
  <div className="relative h-60 w-60 md:h-80 md:w-80 rounded-full flex items-center justify-center overflow-hidden">
    {/* Animated gradient background */}
    <motion.div
      animate={{
        background: [
          'linear-gradient(135deg, #4c1d95 0%, #1e1b4b 50%, #7c3aed 100%)',
          'linear-gradient(135deg, #7c3aed 0%, #4c1d95 50%, #1e1b4b 100%)',
          'linear-gradient(135deg, #1e1b4b 0%, #7c3aed 50%, #4c1d95 100%)',
        ],
      }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
      className="absolute inset-0 rounded-full"
    />
    <div className="absolute inset-0 border border-white/10 rounded-full" />

    {/* AI Brain icon */}
    <motion.div
      animate={{ y: [-8, 8, -8] }}
      transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
      className="relative z-10 flex flex-col items-center gap-4"
    >
      <div className="relative">
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -inset-4 bg-violet-500/30 rounded-full blur-2xl"
        />
        <BrainCircuit className="w-20 h-20 md:w-28 md:h-28 text-violet-300 relative z-10" />
      </div>
      <div className="flex items-center gap-2">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="h-2 w-2 rounded-full bg-violet-400"
        />
        <span className="text-xs text-violet-300 font-medium tracking-wider uppercase">
          IA Ativa
        </span>
      </div>
    </motion.div>
  </div>
);

const ProductVisual = ({ data, isLeft }: { data: ProductData; isLeft: boolean }) => (
  <motion.div layout="position" className="relative group shrink-0">
    {/* Animated Rings */}
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      className={`absolute inset-[-20%] rounded-full border border-dashed border-white/10 ${data.colors.ring}`}
    />
    <motion.div
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      className={`absolute inset-0 rounded-full bg-gradient-to-br ${data.colors.gradient} blur-2xl opacity-40`}
    />

    {/* Image Container */}
    <div
      className="relative h-60 w-60 md:h-[380px] md:w-[380px] rounded-full border border-white/5 shadow-2xl flex items-center justify-center overflow-hidden backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
    >
      <motion.div
        animate={{ y: [-10, 10, -10] }}
        transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
        className="relative z-10 w-full h-full flex items-center justify-center"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={data.id}
            variants={ANIMATIONS.image(isLeft)}
            initial="initial"
            animate="animate"
            exit="exit"
            className="w-full h-full flex items-center justify-center"
          >
            {data.id === 'camera' ? <CameraVisual /> : <IAVisual />}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>

    {/* Status Label */}
    <motion.div
      layout="position"
      className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 bg-zinc-950/80 px-4 py-2 rounded-full border border-white/5 backdrop-blur">
        <span className={`h-1.5 w-1.5 rounded-full ${data.colors.glow} animate-pulse`} />
        {data.stats.connectionStatus}
      </div>
    </motion.div>
  </motion.div>
);

const ProductDetails = ({ data, isLeft }: { data: ProductData; isLeft: boolean }) => {
  const alignClass = isLeft ? 'items-start text-left' : 'items-end text-right';
  const flexDirClass = isLeft ? 'flex-row' : 'flex-row-reverse';
  const barColorClass = isLeft ? 'left-0 bg-orange-500' : 'right-0 bg-violet-500';

  return (
    <motion.div
      variants={ANIMATIONS.container}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`flex flex-col ${alignClass}`}
    >
      <motion.h2
        variants={ANIMATIONS.item}
        className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2"
      >
        {data.id === 'camera' ? 'Hardware' : 'Inteligência Artificial'}
      </motion.h2>
      <motion.h1
        variants={ANIMATIONS.item}
        className="text-3xl md:text-5xl font-bold tracking-tight mb-2"
        style={{
          backgroundImage: 'linear-gradient(to bottom, #ffffff, #71717a)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {data.title}
      </motion.h1>
      <motion.p
        variants={ANIMATIONS.item}
        className={`text-zinc-400 mb-8 max-w-sm leading-relaxed ${isLeft ? 'mr-auto' : 'ml-auto'}`}
      >
        {data.description}
      </motion.p>

      {/* Feature Grid */}
      <motion.div
        variants={ANIMATIONS.item}
        className="w-full space-y-6 bg-zinc-900/40 p-6 rounded-2xl border border-white/5 backdrop-blur-sm"
      >
        {data.features.map((feature, idx) => (
          <div key={feature.label} className="group">
            <div className={`flex items-center justify-between mb-3 text-sm ${flexDirClass}`}>
              <div
                className={`flex items-center gap-2 ${feature.value > 50 ? 'text-zinc-200' : 'text-zinc-400'}`}
              >
                <feature.icon size={16} /> <span>{feature.label}</span>
              </div>
              <span className="font-mono text-xs text-zinc-500">{feature.value}%</span>
            </div>
            <div className="relative h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${feature.value}%` }}
                transition={{ duration: 1, delay: 0.4 + idx * 0.15 }}
                className={`absolute top-0 bottom-0 ${barColorClass} opacity-80`}
              />
            </div>
          </div>
        ))}

        <div className={`pt-4 flex ${isLeft ? 'justify-start' : 'justify-end'}`}>
          <a
            href="/contract-camera"
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-300 hover:text-white transition-colors group no-underline"
          >
            {isLeft ? <Camera size={14} /> : <BrainCircuit size={14} />}
            {isLeft ? 'Saiba Mais' : 'Conheça nossa IA'}
            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </motion.div>

      {/* Extra info */}
      <motion.div
        variants={ANIMATIONS.item}
        className={`mt-6 flex items-center gap-3 text-zinc-500 ${flexDirClass}`}
      >
        <Clock size={16} />
        <span className="text-sm font-medium">
          {isLeft ? 'Gravação 24h contínua' : 'Evoluindo constantemente'}
        </span>
      </motion.div>
    </motion.div>
  );
};

const Switcher = ({
  activeId,
  onToggle,
}: {
  activeId: ProductId;
  onToggle: (id: ProductId) => void;
}) => {
  const options = Object.values(PRODUCT_DATA).map((p) => ({ id: p.id, label: p.label }));

  return (
    <div className="flex justify-center mt-12 md:mt-16">
      <motion.div
        layout
        className="flex items-center gap-1 p-1.5 rounded-full bg-zinc-900/80 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5"
      >
        {options.map((opt) => (
          <motion.button
            key={opt.id}
            onClick={() => onToggle(opt.id)}
            whileTap={{ scale: 0.96 }}
            className="relative w-28 h-12 rounded-full flex items-center justify-center text-sm font-medium focus:outline-none bg-transparent border-none cursor-pointer"
          >
            {activeId === opt.id && (
              <motion.div
                layoutId="showcase-surface"
                className="absolute inset-0 rounded-full bg-gradient-to-b from-white/10 to-white/5 shadow-inner"
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              />
            )}
            <span
              className={`relative z-10 transition-colors duration-300 ${
                activeId === opt.id ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {opt.label}
            </span>
            {activeId === opt.id && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute -bottom-1 h-1 w-6 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent"
              />
            )}
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
};

// =========================================
// 4. MAIN COMPONENT
// =========================================

export default function ProductShowcase() {
  const [activeSide, setActiveSide] = useState<ProductId>('camera');

  const currentData = PRODUCT_DATA[activeSide];
  const isLeft = activeSide === 'camera';

  return (
    <section className="relative w-full overflow-hidden py-16 md:py-24" style={{ backgroundColor: '#0a0a0a' }}>
      <BackgroundGradient isLeft={isLeft} />

      <div className="relative z-10 w-full px-6 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-orange-400 text-sm font-bold uppercase tracking-widest mb-4">
            Como funciona
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
            Da quadra para a tela
          </h2>
          <p className="mt-4 text-zinc-500 max-w-lg mx-auto">
            Conheça os dois pilares que fazem a Apertai funcionar
          </p>
        </div>

        <motion.div
          layout
          transition={{ type: 'spring', bounce: 0, duration: 0.9 }}
          className={`flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 lg:gap-32 w-full ${
            isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
          }`}
        >
          <ProductVisual data={currentData} isLeft={isLeft} />

          <motion.div layout="position" className="w-full max-w-md">
            <AnimatePresence mode="wait">
              <ProductDetails key={activeSide} data={currentData} isLeft={isLeft} />
            </AnimatePresence>
          </motion.div>
        </motion.div>

        <Switcher activeId={activeSide} onToggle={setActiveSide} />
      </div>
    </section>
  );
}
